import {
  Button,
  buttonStylesPrimary,
  buttonStylesSecondary,
  buttonStylesWidthXSmall,
  IconName,
  loaderStylesSpinnerButtonPrimary,
  loaderStylesSpinnerButtonSecondary,
} from '@h2oai/ui-kit';

import { hasOp } from '../../../aiem/engine/conditions';
import { useEngine } from '../../../aiem/engine/hooks';
import { AIEMOpType } from '../../../aiem/engine/types';
import { isTransitionalState } from '../../../aiem/engine/utils';
import { Engine_State, Engine_Type } from '../../../aiem/gen/ai/h2o/engine/v1/engine_pb';
import { AIEMUserActionType } from '../../../aiem/userActions';
import {
  restrictionTooltipMessage,
  usePermissions,
} from '../../../authz/providers/PermissionsProvider/PermissionsProvider';
import { useUser } from '../../../utils/hooks';
import { engineButtonTestIdSuffixes, getEngineButtonTestId } from '../../../utils/utils';
import { EngineActionButtonWidgetProps } from './types';

export const aiEnginesActionButtonWidgetUserActions = [
  AIEMUserActionType.DAI_ENGINE_RESUME,
  AIEMUserActionType.DAI_ENGINE_PAUSE,
];

export const EngineActionButtonWidget = (props: EngineActionButtonWidgetProps) => {
  const { engine, modifyEngine } = props;
  const { id } = useUser();
  const { accessEngine } = useEngine();
  const [canResumeDAI, canPauseDAI] = usePermissions(aiEnginesActionButtonWidgetUserActions);

  const isNotebookEngine = engine.type === Engine_Type.NOTEBOOK;
  const isRunning = engine.state === Engine_State.RUNNING;
  const isPaused = engine.state === Engine_State.PAUSED;

  const loading = isTransitionalState(engine.state);
  const isCreator = id === engine.creator?.split('/')[1];

  const pauseDisabled = !canPauseDAI || !hasOp(AIEMOpType.pause, engine.type, engine.state);
  const resumeDisabled = !canResumeDAI || !hasOp(AIEMOpType.resume, engine.type, engine.state);

  const checkAccess = async () => {
    if (!engine.name) return;
    const result = await accessEngine(engine.name);
    if (result) window.open(result.uri, '_blank', 'noopener,noreferrer');
  };

  const visitButtonBase = {
    title: 'Visit',
    text: 'Visit',
    iconName: IconName.ArrowUpRight,
    'data-test': getEngineButtonTestId(engine, engineButtonTestIdSuffixes.visit),
    styles: buttonStylesPrimary,
  };

  const visitButtonProps = isNotebookEngine
    ? {
        ...visitButtonBase,
        disabled: !isRunning || !engine.visitable,
        onClick: checkAccess,
        tooltip: !isCreator && isRunning ? 'The engine is private' : undefined,
      }
    : {
        ...visitButtonBase,
        disabled: !isRunning || !engine.loginUrl || !isCreator,
        href: engine.loginUrl,
        target: '_blank',
        type: 'link',
      };

  const actionButton = isPaused ? (
    <Button
      styles={[buttonStylesPrimary, buttonStylesWidthXSmall]}
      iconName={IconName.MSNVideos}
      text="Resume"
      disabled={resumeDisabled}
      data-test={getEngineButtonTestId(engine, engineButtonTestIdSuffixes.resume)}
      onClick={() => modifyEngine(engine, AIEMOpType.resume)}
      loading={loading}
      loaderProps={{
        label: 'Loading...',
        styles: loaderStylesSpinnerButtonPrimary,
      }}
      tooltip={restrictionTooltipMessage(!canResumeDAI)}
    />
  ) : (
    <Button
      styles={[buttonStylesSecondary, buttonStylesWidthXSmall]}
      iconName={IconName.Pause}
      text="Pause"
      disabled={pauseDisabled}
      data-test={getEngineButtonTestId(engine, engineButtonTestIdSuffixes.pause)}
      onClick={() => modifyEngine(engine, AIEMOpType.pause)}
      loading={loading}
      loaderProps={{
        label: 'Loading...',
        styles: loaderStylesSpinnerButtonSecondary,
      }}
      tooltip={restrictionTooltipMessage(!canPauseDAI)}
    />
  );

  return (
    <div style={{ display: 'flex', gap: 8 }}>
      <Button {...visitButtonProps} />
      {actionButton}
    </div>
  );
};
